import type { PosCartItem } from '@/lib/services/carts/carts.types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import ImageThumbnail from '@/components/media/ImageThumbnail'
import { Typography } from '@/components/ui/typography'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { subscriptionFrequencyOptions } from '@/lib/subscription-frequency'
import { formatCurrency } from '@/lib/utils'
import { Minus, Pencil, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  queuePosCartLineQuantity,
  queuePosCartLineRemoval,
  useIsCartMutating,
  useIsCartSaving,
  setCartQuantityEditPending,
} from '@/lib/services/carts/carts.query'
import { toast } from 'sonner'
import {
  getPosInventoryErrorMessage,
  getPosVariantOnHand,
  getRemainingVariantInventory,
} from '@/lib/pos/inventory'
import { isSubscriptionPackageCartItem } from '@/lib/pos/subscriptions'
import type { OrderChangeLabel } from '@/lib/pos/order-change'

// Typing is for everyday counts; larger quantities still go through +.
const MAX_TYPED_QUANTITY = 999

function parseQuantityDraft(draft: string | null) {
  const value = draft?.trim() ?? ''

  return /^\d+$/.test(value) ? Number(value) : null
}

function getSubscriptionFrequencyLabel(frequency: string) {
  return subscriptionFrequencyOptions.find((option) => option.id === frequency)?.name ?? frequency
}

type CartItemRowProps = {
  item: PosCartItem
  disabled?: boolean
  quantityByVariantId?: Record<number, number>
  onEdit?: ((item: PosCartItem) => void) | null
  /** How an order change's draft line differs from the order: added, or changed. */
  changeLabel?: OrderChangeLabel | null
}

export function CartItemRow({
  item,
  disabled = false,
  quantityByVariantId = {},
  onEdit = null,
  changeLabel = null,
}: CartItemRowProps) {
  const organizationId = useRouteOrganizationId()
  const isCartMutating = useIsCartMutating()
  const isCartSaving = useIsCartSaving()
  // Typed quantity while the field is being edited; null shows the cart's quantity.
  const [quantityDraft, setQuantityDraft] = useState<string | null>(null)
  // The typed quantity from the moment it is committed until its save finishes. The field
  // shows it meanwhile, because the cart only takes the new quantity once the save starts.
  const [savingQuantity, setSavingQuantity] = useState<number | null>(null)

  // A typed value the cart lock kept from saving, shown so the florist knows to re-enter it.
  const [lockedQuantityDraft, setLockedQuantityDraft] = useState<number | null>(null)

  // A locked cart (for example during payment) drops any unsaved typed value, whether or not
  // the browser blurs the field when it disables.
  if (disabled && quantityDraft !== null) {
    const typedQuantity = parseQuantityDraft(quantityDraft)
    const wouldHaveSaved =
      typedQuantity !== null &&
      typedQuantity >= 1 &&
      typedQuantity <= MAX_TYPED_QUANTITY &&
      typedQuantity !== item.quantity
    setLockedQuantityDraft(wouldHaveSaved ? typedQuantity : null)
    setQuantityDraft(null)
  }

  if (!disabled && lockedQuantityDraft !== null) {
    setLockedQuantityDraft(null)
  }

  const display = getLineItemDisplay(item)
  const image = display.image
  const quantity = item.quantity
  const cartItemId = item.id
  const cartId = item.cart_id
  const variant = item.variant
  const isSubscriptionPackage = Boolean(isSubscriptionPackageCartItem(item))
  const selectedSubscriptionFrequency = item.subscription_frequency ?? ''
  const subscriptionFrequencyLabel = selectedSubscriptionFrequency
    ? getSubscriptionFrequencyLabel(selectedSubscriptionFrequency)
    : null
  const onHand = getPosVariantOnHand(variant)
  const inCartQuantity = Number((variant ? quantityByVariantId[variant.id] : 0) ?? 0)
  const remainingInventory = getRemainingVariantInventory(variant, quantityByVariantId)
  const isIncrementDisabled = remainingInventory !== null && remainingInventory < 1
  const isInventoryStale = onHand !== null && inCartQuantity > onHand
  const secondaryText = isSubscriptionPackage
    ? [display.badgeLabel, subscriptionFrequencyLabel].filter(Boolean).join(' · ')
    : display.isCustom
      ? (display.description ?? display.badgeLabel ?? null)
      : (display.badgeLabel ?? null)
  const inventoryHelperText =
    !isSubscriptionPackage && isIncrementDisabled && onHand !== null
      ? onHand > 0
        ? `Only ${onHand} in stock`
        : 'Out of stock'
      : null
  const hasQuantityEdit = quantityDraft !== null || savingQuantity !== null

  // Mark the cart busy while this row has a typed quantity on its way. The click that blurs
  // the field (and saves it) is usually on another cart control, which must not start a second
  // cart change at the same time. Synchronizes with the shared store in carts.query.
  useEffect(() => {
    if (!hasQuantityEdit) {
      return undefined
    }

    setCartQuantityEditPending(cartItemId, true)
    return () => setCartQuantityEditPending(cartItemId, false)
  }, [cartItemId, hasQuantityEdit])

  const updateQuantity = async (newQuantity: number) => {
    if (isSubscriptionPackage) {
      return
    }

    if (newQuantity <= 0) {
      try {
        await queuePosCartLineRemoval(organizationId, { cartId, cartItemId })
      } catch (error) {
        toast.error('Failed to remove item', {
          description: getPosInventoryErrorMessage(error) || 'Please try again',
        })
      }
      return
    }

    try {
      const result = await queuePosCartLineQuantity(organizationId, {
        cartId,
        cartItemId,
        quantity: newQuantity,
      })

      if (result?.adjusted || result?.removed) {
        toast.warning('Inventory updated', {
          description: result.message,
        })
      }
    } catch (error) {
      toast.error('Failed to update item', {
        description: getPosInventoryErrorMessage(error) || 'Please try again',
      })
    }
  }

  const isWithinTypingLimit = (typedQuantity: number) => {
    if (typedQuantity <= MAX_TYPED_QUANTITY) {
      return true
    }

    toast.error('Quantity is too large', {
      description: `Type up to ${MAX_TYPED_QUANTITY}, or use + to go higher.`,
    })
    return false
  }

  const hasStockFor = (newQuantity: number) => {
    if (remainingInventory === null || newQuantity - quantity <= remainingInventory) {
      return true
    }

    toast.error('Not enough stock', {
      description: onHand !== null && onHand > 0 ? `Only ${onHand} in stock` : 'Out of stock',
    })
    return false
  }

  const commitQuantityDraft = async () => {
    const newQuantity = parseQuantityDraft(quantityDraft)

    if (
      disabled ||
      newQuantity === null ||
      newQuantity < 1 ||
      newQuantity === quantity ||
      !isWithinTypingLimit(newQuantity) ||
      !hasStockFor(newQuantity)
    ) {
      setQuantityDraft(null)
      return
    }
    // Keep the edit open until the save finishes so the cart never looks idle in between.
    setSavingQuantity(newQuantity)
    setQuantityDraft(null)
    try {
      await updateQuantity(newQuantity)
    } finally {
      setSavingQuantity(null)
    }
  }

  const handleRemove = async () => {
    try {
      await queuePosCartLineRemoval(organizationId, { cartId, cartItemId })
    } catch (error) {
      toast.error('Failed to remove item', {
        description: getPosInventoryErrorMessage(error) || 'Please try again',
      })
    }
  }

  // This row's own typed quantity also counts, so its buttons wait even where the shared
  // busy state hasn't caught up yet.
  const isPending = isCartMutating > 0 || disabled || hasQuantityEdit

  return (
    <div className="@container border-b p-2 last:border-b-0">
      <div className="flex flex-col gap-2 @[36rem]:flex-row @[36rem]:items-center @[36rem]:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Product Image */}
          {image && <ImageThumbnail media={image} className="h-12 w-12 shrink-0 shadow-none" />}
          {/* Item Name */}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <Typography variant="label" className="line-clamp-2">
                {display.primaryName}
              </Typography>
              {changeLabel ? (
                <Badge variant="info" size="xs" className="shrink-0">
                  {changeLabel}
                </Badge>
              ) : null}
            </div>
            {secondaryText && (
              <Typography variant="label" tone="muted" className="line-clamp-1">
                {secondaryText}
              </Typography>
            )}
            {lockedQuantityDraft !== null && (
              <Typography variant="label" tone="warning" className="mt-1 block">
                {`Quantity ${lockedQuantityDraft} wasn't saved because the cart is locked.`}
              </Typography>
            )}
            {inventoryHelperText && (
              <Typography
                variant="label"
                tone={isInventoryStale ? 'destructive' : 'warning'}
                data-inventory-state={isInventoryStale ? 'stale' : 'low'}
                className="mt-1 block"
              >
                {inventoryHelperText}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 @[36rem]:shrink-0">
          {!isSubscriptionPackage ? (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => updateQuantity(quantity - 1)}
                // While a typed value is open, clicking here only blurs the field, which saves it.
                disabled={isPending}
                aria-label={`Decrease quantity for ${display.primaryName}`}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <Input
                size="sm"
                inputMode="numeric"
                aria-label={`Quantity for ${display.primaryName}`}
                className="w-14 shrink-0 text-center"
                value={quantityDraft ?? String(savingQuantity ?? quantity)}
                disabled={disabled}
                // Read-only, not disabled, while a cart save runs: it keeps focus and still blurs.
                readOnly={isCartSaving > 0}
                onFocus={(event) => event.currentTarget.select()}
                // Keep the focus selection: a click's mouseup would otherwise drop it, so typing appended digits.
                onMouseUp={(event) => event.preventDefault()}
                onChange={(event) => setQuantityDraft(event.target.value)}
                onBlur={commitQuantityDraft}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.currentTarget.blur()
                  } else if (event.key === 'Escape') {
                    const input = event.currentTarget
                    setQuantityDraft(null)
                    // Reselect once the restored value renders so the next keystroke replaces it.
                    requestAnimationFrame(() => input.select())
                  }
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => updateQuantity(quantity + 1)}
                disabled={isPending || isIncrementDisabled}
                aria-label={`Increase quantity for ${display.primaryName}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          ) : null}

          {/* Price */}
          <Typography variant="labelStrong" align="right" className="min-w-fit flex-1 shrink-0">
            {item.subtotal === undefined ? null : formatCurrency(item.subtotal)}
          </Typography>

          <div className="ml-auto flex shrink-0 items-center">
            {onEdit ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(item)}
                disabled={isPending}
                aria-label={`Edit ${display.primaryName}`}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            ) : null}

            {/* Remove Button; an order change lists a removed order item crossed out, with a way to restore it. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRemove}
              disabled={isPending}
              aria-label={`Remove ${display.primaryName}`}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
